import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useContactInquiry,
  ContactInquiryProvider,
  INQUIRY_CATEGORIES,
  INQUIRY_ATTACHMENT_RULES,
} from '../context/ContactInquiryContext';
import { toast } from 'sonner';
import { Mail, MapPin, Send, CheckCircle, Paperclip, X, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import SEO from '../components/SEO';
import CorporateIdentityBlock from '../components/CorporateIdentityBlock';
import { corporateIdentityAPI } from '../utils/api';

// The registration details a visitor (or a payment provider reviewing the
// site) needs to identify who operates TreatX. SSOT: `legal_entities` through
// GET /api/public/corporate-identity — the same payload the About page reads.
// Nothing is hard-coded here, and a field the CMS has not published simply
// does not appear.
const CorporateIdentityPanel = () => {
  const { t } = useTranslation();
  const [entity, setEntity] = useState(null);

  useEffect(() => {
    let cancelled = false;
    corporateIdentityAPI.getDefault()
      .then((res) => { if (!cancelled) setEntity(res?.data || null); })
      .catch(() => { if (!cancelled) setEntity(null); });
    return () => { cancelled = true; };
  }, []);

  if (!entity) return null;

  const idLabels = {
    blockHeading: t('corporateIdentity.blockHeading', 'Corporate Identity'),
    blockSubheading: t('corporateIdentity.blockSubheading', 'Public registration details of the entity operating this platform.'),
    commercialTitle: t('corporateIdentity.commercialTitle', 'Commercial title'),
    brand: t('corporateIdentity.brand', 'Brand'),
    mersis: t('corporateIdentity.mersis', 'MERSİS number'),
    tradeRegistry: t('corporateIdentity.tradeRegistry', 'Trade registry no.'),
    taxOffice: t('corporateIdentity.taxOffice', 'Tax office'),
    taxId: t('corporateIdentity.taxId', 'Tax ID'),
    chamber: t('corporateIdentity.chamber', 'Chamber'),
    chamberRegistry: t('corporateIdentity.chamberRegistry', 'Chamber registry no.'),
    registeredAddress: t('corporateIdentity.registeredAddress', 'Registered address'),
    generalEmail: t('corporateIdentity.generalEmail', 'General enquiries'),
    kvkkEmail: t('corporateIdentity.kvkkEmail', 'Data protection (KVKK) contact'),
    kep: t('corporateIdentity.kep', 'Registered Electronic Mail (KEP)'),
    phone: t('corporateIdentity.phone', 'Phone'),
    applicationInProgress: t('corporateIdentity.pending', 'Application in progress'),
  };

  return (
    <div className="mt-8" data-testid="contact-corporate-identity">
      <CorporateIdentityBlock entity={entity} labels={idLabels} variant="full" />
    </div>
  );
};

const PRIVACY_CATEGORY = 'privacy_data_request';

const fileExt = (name) => {
  const n = String(name || '');
  return n.includes('.') ? n.split('.').pop().toLowerCase() : '';
};

const prettySize = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const ContactForm = () => {
  const { t } = useTranslation();
  const { submitInquiry } = useContactInquiry();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [files, setFiles] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    category: 'general',
  });

  const isPrivacyRequest = formData.category === PRIVACY_CATEGORY;

  const categoryLabel = (value) =>
    t(`contact.categories.${value}`, INQUIRY_CATEGORIES.find((c) => c.value === value)?.label || value);

  const addFiles = (incoming) => {
    const accepted = [];
    for (const f of incoming) {
      if (!INQUIRY_ATTACHMENT_RULES.allowedExtensions.includes(fileExt(f.name))) {
        toast.error(t('contact.attachments.errorType', { name: f.name, defaultValue: `${f.name}: only PDF, JPG and PNG files are allowed.` }));
        continue;
      }
      if (f.size > INQUIRY_ATTACHMENT_RULES.maxFileSizeBytes) {
        toast.error(t('contact.attachments.errorSize', { name: f.name, defaultValue: `${f.name}: file is larger than the 10 MB limit.` }));
        continue;
      }
      accepted.push(f);
    }
    setFiles((prev) => {
      const room = INQUIRY_ATTACHMENT_RULES.maxFiles - prev.length;
      if (accepted.length > room) {
        toast.error(t('contact.attachments.errorCount', { max: INQUIRY_ATTACHMENT_RULES.maxFiles, defaultValue: `You can attach at most ${INQUIRY_ATTACHMENT_RULES.maxFiles} files.` }));
      }
      return [...prev, ...accepted.slice(0, Math.max(room, 0))];
    });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message.trim()) {
      toast.error(t('contact.requiredError', 'Please provide your name, email and message'));
      return;
    }

    setLoading(true);
    try {
      await submitInquiry(
        { ...formData, subject: formData.subject.trim() || categoryLabel(formData.category) },
        files
      );
      toast.success(t('contact.successToast', "Message sent successfully! We'll respond within 24 hours."));
      setSubmitted(true);
      setFormData({ name: '', email: '', subject: '', message: '', category: 'general' });
      setFiles([]);
    } catch (error) {
      const data = error?.response?.data;
      const detail = data?.detail;
      const msg = data?.message || (detail && (detail.message || detail));
      toast.error(typeof msg === 'string' ? msg : t('contact.failedError', 'Failed to send message'));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-200 text-center" data-testid="contact-success">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-600" />
        </div>
        <h3 className="text-xl font-bold text-secondary mb-2">{t('contact.successTitle', 'Message Sent!')}</h3>
        <p className="text-foreground/70 mb-6">
          {t('contact.successBody', 'Thank you for reaching out. Our team will review your message and respond within 24 hours.')}
        </p>
        <Button
          onClick={() => setSubmitted(false)}
          variant="outline"
          data-testid="send-another-btn"
        >
          {t('contact.sendAnother', 'Send Another Message')}
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-200" data-testid="contact-form">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">{t('contact.name', 'Name')}</label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder={t('contact.namePlaceholder', 'Your name')}
            className="h-12"
            required
            data-testid="name-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">{t('contact.email', 'Email')}</label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="your@email.com"
            className="h-12"
            required
            data-testid="email-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">{t('contact.category', 'Category')}</label>
          <select
            value={formData.category}
            onChange={(e) => {
              const category = e.target.value;
              setFormData({ ...formData, category });
              if (category !== PRIVACY_CATEGORY) setFiles([]);
            }}
            className="w-full h-12 border rounded-lg px-3 text-foreground bg-white"
            data-testid="category-select"
          >
            {INQUIRY_CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>{categoryLabel(cat.value)}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            {t('contact.subject', 'Subject')}{' '}
            <span className="text-foreground/50 font-normal">({t('contact.optional', 'optional')})</span>
          </label>
          <Input
            value={formData.subject}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            placeholder={t('contact.subjectPlaceholder', 'How can we help?')}
            className="h-12"
            data-testid="subject-input"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">{t('contact.message', 'Message')}</label>
          <Textarea
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            placeholder={t('contact.messagePlaceholder', 'Tell us more about your inquiry...')}
            rows={6}
            required
            data-testid="message-input"
          />
        </div>

        {/* TREATX-DATA-PROTECTION-REQUEST-01 — privacy/data-protection guidance.
            The application form itself lives on the normal public Legal
            surface (Agreement Management SSOT), NOT here. */}
        {isPrivacyRequest && (
          <div className="rounded-xl border border-primary/20 bg-primary/5 p-4" data-testid="data-protection-notice">
            <div className="flex items-start gap-2">
              <ShieldCheck className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <p className="text-sm text-foreground/80">
                {t('contact.dataProtection.notice', 'Describe your request in the message field — that is enough to submit it. You may optionally attach the completed TreatX application form or another supporting document. We do not ask for an identity document.')}
              </p>
            </div>
          </div>
        )}

        {/* Optional attachments — shown only for the privacy / data-protection
            request type (Owner-scoped V1). */}
        {isPrivacyRequest && (
        <div className="space-y-3" data-testid="attachments-block">
          <label className="block text-sm font-medium text-foreground">
            {t('contact.attachments.label', 'Attachments')}{' '}
            <span className="text-foreground/50 font-normal">({t('contact.optional', 'optional')})</span>
          </label>
          <p className="text-xs text-foreground/60" data-testid="attachments-hint">
            {t('contact.attachments.hint', 'PDF, JPG or PNG. Up to 5 files, max 10 MB each.')}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
            onChange={(e) => addFiles(Array.from(e.target.files || []))}
            className="hidden"
            data-testid="attachment-file-input"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={files.length >= INQUIRY_ATTACHMENT_RULES.maxFiles}
            data-testid="attachment-add-button"
          >
            <Paperclip className="w-4 h-4 mr-2" />
            {t('contact.attachments.add', 'Attach Files')}
          </Button>
          {files.length > 0 && (
            <ul className="space-y-2" data-testid="attachment-list">
              {files.map((f, idx) => (
                <li
                  key={`${f.name}-${idx}`}
                  className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
                  data-testid={`attachment-item-${idx}`}
                >
                  <span className="min-w-0 flex-1 truncate text-sm text-foreground/80">{f.name}</span>
                  <span className="text-xs text-foreground/50 flex-shrink-0">{prettySize(f.size)}</span>
                  <button
                    type="button"
                    onClick={() => removeFile(idx)}
                    className="text-foreground/50 hover:text-red-600 flex-shrink-0"
                    aria-label={t('contact.attachments.remove', 'Remove')}
                    data-testid={`attachment-remove-${idx}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        )}

        <Button
          type="submit"
          disabled={loading}
          className="w-full h-12 bg-primary hover:bg-primary/90 rounded-full"
          data-testid="submit-contact-button"
        >
          {loading
            ? t('contact.sending', 'Sending...')
            : t('contact.send', 'Send Message')}
          <Send className="w-5 h-5 ml-2" />
        </Button>
      </form>
    </div>
  );
};

const ContactPageContent = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-primary/5" data-testid="contact-page">
      <SEO
        title="Contact TreatX"
        description="Reach the TreatX team for support, provider partnerships or general enquiries. Response within 24 hours."
        canonicalPath="/contact"
      />
      <div className="container-custom section">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold text-secondary mb-4">{t('contact.title', 'Get in Touch')}</h1>
            <p className="text-foreground/70 text-lg">{t('contact.subtitle', 'Have questions? We are here to help!')}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <ContactForm />

            {/* Contact Info */}
            <div className="space-y-8">
              <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-200">
                <h2 className="text-2xl font-bold text-secondary mb-6">{t('contact.infoTitle', 'Contact Information')}</h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4" data-testid="contact-email-info">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary flex-shrink-0">
                      <Mail className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-secondary mb-1">{t('contact.email', 'Email')}</h3>
                      <a href="mailto:info@treatx.health" className="text-primary hover:underline">info@treatx.health</a>
                      <br />
                      <a href="mailto:support@treatx.health" className="text-primary hover:underline">support@treatx.health</a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4" data-testid="contact-website">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary flex-shrink-0">
                      <MapPin className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-secondary mb-1">{t('contact.website', 'Website')}</h3>
                      <p className="text-foreground/70">treatx.health</p>
                    </div>
                  </div>
                </div>
              </div>

              <CorporateIdentityPanel />

              <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl p-8">
                <h3 className="font-semibold text-secondary mb-2">{t('contact.helpTitle', 'Need Immediate Help?')}</h3>
                <p className="text-foreground/70 mb-4">
                  {t('contact.helpBody', 'Our support team is available 24/7 to answer your questions and assist with your medical tourism journey.')}
                </p>
                <p className="text-sm text-foreground/60">
                  {t('contact.helpResponse', 'Average response time: 2-4 hours')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrapper with provider
const ContactPage = () => {
  return (
    <ContactInquiryProvider>
      <ContactPageContent />
    </ContactInquiryProvider>
  );
};

export default ContactPage;
